import React, { PropsWithChildren, useState } from 'react';
import { QueryData } from 'lib/api/utils';
import { useFilteredSims } from 'lib/hooks/sim/sim.queries';
import AddTagsModal from '../AddTagsModal/AddTagsModal';
import AddClientModal from '../AddClientModal/AddClientModal';
import DeleteSimModal from '../DeleteSimModal/DeleteSimModal';

type SimRecord = QueryData<typeof useFilteredSims>['records'][number];

type ModalsProviderProps = {};

type ModalsProviderState = {
  targetSim: SimRecord | null;
};

type ModalsProviderActions = {
  setTargetSim: (sim: SimRecord | null) => void;
  openAddTagsModal: (sim: SimRecord) => void;
  closeAddTagsModal: () => void;
  openClientModal: (sim: SimRecord) => void;
  closeClientModal: () => void;
  openDeleteModal: (sim: SimRecord) => void;
  closeDeleteModal: () => void;
};

type ModalProvidersContextType = ModalsProviderState &
  ModalsProviderActions &
  ModalsProviderProps & {};

const ModalsProviderContext =
  React.createContext<ModalProvidersContextType | null>(null);

/**
 * Provider component for the Modals to be used in the YourSims View
 *
 * @description
 * We are using a provider so that we have a centralized place to control all the modals to be used in the YourSims View. This is to avoid having to pass the same props to all the modals.
 *
 * It also allows us to render the modals only once instead rendering for each sim record.
 *
 * @see `YourSims/hooks.ts` -> `useModalsContext()` to consume the context.
 */
function ModalsProvider({
  children,
  ...props
}: PropsWithChildren<ModalsProviderProps>) {
  const [targetSim, setTargetSim] = useState<SimRecord | null>(null);
  const [addTagsModalOpen, setAddTagsModalOpen] = useState(false);
  const [addClientModalOpen, setAddClientModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const openAddTagsModal = (sim: SimRecord) => {
    setTargetSim(sim);
    setAddTagsModalOpen(true);
  };

  const closeAddTagsModal = () => {
    setAddTagsModalOpen(false);
    setTargetSim(null);
  };

  const openClientModal = (sim: SimRecord) => {
    setTargetSim(sim);
    setAddClientModalOpen(true);
  };

  const closeClientModal = () => {
    setAddClientModalOpen(false);
    setTargetSim(null);
  };

  const openDeleteModal = (sim: SimRecord) => {
    setTargetSim(sim);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setTargetSim(null);
  };

  return (
    <ModalsProviderContext.Provider
      value={{
        targetSim,
        setTargetSim,
        openAddTagsModal,
        closeAddTagsModal,
        openClientModal,
        closeClientModal,
        openDeleteModal,
        closeDeleteModal,
        ...props,
      }}
    >
      {children}

      <AddTagsModal isOpen={addTagsModalOpen} onClose={closeAddTagsModal} />

      <AddClientModal isOpen={addClientModalOpen} onClose={closeClientModal} />

      <DeleteSimModal isOpen={deleteModalOpen} onClose={closeDeleteModal} />
    </ModalsProviderContext.Provider>
  );
}

export default ModalsProviderContext;

export { ModalsProvider };
